import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  Body,
  Button,
  DataTable,
  Pill,
  SearchInput,
  SectionHead,
  Stack,
  type ColumnDef,
} from '@leanin/ui';
import { formatDateMedium } from '@/lib/format-date';
import {
  GROUP_INVITES_PAGE_SIZE,
  groupKeys,
  type GroupInviteRow,
} from '@/lib/queries/groups';
import { useRevokeGroupInvite } from '@/lib/mutations/groups';
import { useDebouncedValue } from '@/lib/use-debounced-value';

// Outstanding per-email invites for a circle/network, on the admin Members
// screen. Server-paged (offset — the numbered pager jumps to arbitrary
// pages) with server-side email search; mirrors the members table above it.

type GroupKind = 'circle' | 'network';

const PAGE_SIZE = GROUP_INVITES_PAGE_SIZE;

// Invalidation prefix (inner node) — matches every page/search variant.
function invitesPrefix(kind: GroupKind, groupId: string) {
  return kind === 'circle'
    ? groupKeys.circle.invites(groupId)
    : groupKeys.network.invites(groupId);
}

export function GroupPendingInvitesCard({
  kind,
  groupId,
}: {
  readonly kind: GroupKind;
  readonly groupId: string;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [pageIndex, setPageIndex] = useState(0);
  // Parallel queries, each gated by `kind` via `enabled`. `circle.invitesPage`
  // and `network.invitesPage` are structurally identical leaves differing only
  // by a `'circle' | 'network'` key discriminator; consuming them as one
  // ternary would yield a union TS can't unify at `useQuery`, so we mirror the
  // sibling admin sections' enabled-gated pattern. Only the active branch
  // fetches; `invitesQuery` reads whichever is live.
  const invitesPageParams = { page: pageIndex + 1, q: debouncedSearch };
  const circleQuery = useQuery({
    ...groupKeys.circle.invitesPage(groupId, invitesPageParams),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.invitesPage(groupId, invitesPageParams),
    enabled: kind === 'network',
  });
  const invitesQuery = kind === 'circle' ? circleQuery : networkQuery;
  const revoke = useRevokeGroupInvite();
  const [revokeTarget, setRevokeTarget] = useState<{
    inviteId: string;
    email: string;
  } | null>(null);

  const data = invitesQuery.data;
  // Clamp: revoking the last row of the last page (or a stale deep page
  // after a filter change) leaves pageIndex past the end — snap back to
  // the last real page.
  useEffect(() => {
    if (data === undefined) return;
    const pageCount = Math.max(1, Math.ceil(data.totalPending / PAGE_SIZE));
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [data, pageIndex]);

  if (data === undefined) return null;

  const { invites, totalPending } = data;

  const columns: ColumnDef<GroupInviteRow>[] = [
    {
      id: 'invitee',
      header: 'Invitee',
      meta: { mobilePrimary: true },
      cell: ({ row }) => (
        // Long addresses wrap instead of widening the max-content table
        // past its container (the off-screen Actions column bug).
        <Body size="sm" style={{ maxWidth: 320, overflowWrap: 'anywhere' }}>
          {row.original.invitedEmail}
        </Body>
      ),
    },
    {
      id: 'role',
      header: 'Invited as',
      meta: { width: 120 },
      cell: ({ row }) => (
        <Pill tone={row.original.role === 'leader' ? 'burgundy' : 'neutral'}>
          {row.original.role === 'leader' ? 'Leader' : 'Member'}
        </Pill>
      ),
    },
    {
      id: 'sent',
      header: 'Sent',
      meta: { width: 140, nowrap: true },
      cell: ({ row }) => (
        <Body size="sm" color="mute">
          {formatDateMedium(row.original.createdAt)}
        </Body>
      ),
    },
    {
      id: 'expires',
      header: 'Expires',
      meta: { width: 140, nowrap: true },
      cell: ({ row }) => (
        <Body size="sm" color="mute">
          {row.original.expiresAt !== null
            ? formatDateMedium(row.original.expiresAt)
            : '—'}
        </Body>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { width: 110, align: 'end', nowrap: true },
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghostDanger"
          size="sm"
          disabled={revoke.isPending}
          onClick={() =>
            setRevokeTarget({
              inviteId: row.original.inviteId,
              email: row.original.invitedEmail,
            })
          }
        >
          Revoke
        </Button>
      ),
    },
  ];

  const hasAnyPending = totalPending > 0 || debouncedSearch.length > 0;

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <SectionHead title={`Pending invites · ${totalPending}`} />
        <Body size="sm" color="mute">
          Email invitations that haven&apos;t been accepted yet. Recipients can
          join until the invite expires or is revoked.
        </Body>
      </Stack>
      {!hasAnyPending ? (
        <Body size="sm" color="mute">
          No pending invites.
        </Body>
      ) : (
        <Stack gap="md">
          <SearchInput
            placeholder="Search invites by email…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPageIndex(0);
            }}
            style={{ maxWidth: 320 }}
          />
          <DataTable
            data={[...invites]}
            columns={columns}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex,
              onPageChange: setPageIndex,
              rowCount: totalPending,
            }}
          >
            <DataTable.Header />
            <DataTable.Body emptyMessage="No pending invites match." />
            <DataTable.Pagination />
          </DataTable>
        </Stack>
      )}

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRevokeTarget(null);
        }}
        title="Revoke this invite?"
        description={
          revokeTarget !== null
            ? `${revokeTarget.email} will no longer be able to join with this invitation.`
            : ''
        }
        actionLabel="Revoke"
        cancelLabel="Cancel"
        destructive
        onAction={() => {
          if (revokeTarget !== null) {
            revoke.mutate(revokeTarget.inviteId, {
              onSettled: () => {
                void qc.invalidateQueries({
                  queryKey: invitesPrefix(kind, groupId),
                });
              },
            });
            setRevokeTarget(null);
          }
        }}
      />
    </Stack>
  );
}
